/**
 * "Veri %X tutarlı" rozeti — custom-report sayfalarının sağ üstünde.
 *
 * Raporun her mecraya atfettiği HARCAMA ve TRAFİK, o mecranın kendi API'sinin
 * dip toplamıyla karşılaştırılır (+ GA4 oturum & ciro). Günlük kırılım çekilmez;
 * tek seferlik dip toplam yeter, o yüzden hızlıdır.
 *
 * 🔴 Canlı API turu ~30 sn sürüyor (Google Ads python süreci tek başına ~15 sn),
 * bu yüzden sunucu tarafında 1 saat önbelleklenir. Rozet açılışta kendiliğinden
 * sorar; kullanıcı yenile derse `refresh=1` ile zorlar.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw, X } from 'lucide-react';

const API = 'https://api.glowytics.com';
const fmtDate = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const tr = (n) => Math.round(Number(n) || 0).toLocaleString('tr-TR');

const ETIKET = {
  GOOGLE_ADS: 'Google Ads', META_ADS: 'Meta', PINTEREST_ADS: 'Pinterest',
  CRITEO_ADS: 'Criteo', RTB_HOUSE: 'RTB House', TIKTOK_ADS: 'TikTok',
};
const adlandir = (ad) => {
  const [pf, ...rest] = ad.split(' ');
  return ETIKET[pf] ? `${ETIKET[pf]} ${rest.join(' ')}` : ad;
};

export default function ConsistencyBadge({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [acik, setAcik] = useState(false);
  const kutu = useRef(null);

  const getir = useCallback(async (zorla = false) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId || !startDate || !endDate) return;
    setYukleniyor(true); setHata(null);
    try {
      const q = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      if (zorla) q.set('refresh', '1');
      const res = await fetch(`${API}/api/report-audit/consistency?${q}`, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId,
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setHata(e.message); }
    finally { setYukleniyor(false); }
  }, [startDate, endDate]);

  useEffect(() => { getir(false); }, [getir]);

  // Dışına tıklayınca kapansın
  useEffect(() => {
    if (!acik) return undefined;
    const f = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    document.addEventListener('mousedown', f);
    return () => document.removeEventListener('mousedown', f);
  }, [acik]);

  const oran = data?.oran;
  const renk = oran == null ? 'bg-slate-50 text-slate-500 ring-slate-200'
    : oran >= 98 ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : oran >= 95 ? 'bg-amber-50 text-amber-700 ring-amber-200'
    : 'bg-rose-50 text-rose-700 ring-rose-200';
  const Ikon = oran != null && oran >= 98 ? ShieldCheck : ShieldAlert;

  return (
    <div className="relative" ref={kutu}>
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        title="Verinin platform API'leriyle tutarlılığı — ayrıntı için tıkla"
        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium ring-1 shadow-sm transition ${renk}`}
      >
        {yukleniyor && !data
          ? <><Loader2 size={12} className="animate-spin" /> tutarlılık ölçülüyor…</>
          : hata
            ? <><ShieldAlert size={12} /> tutarlılık okunamadı</>
            : <><Ikon size={12} /> Veri %{oran != null ? oran.toFixed(1) : '—'} tutarlı</>}
      </button>

      {acik && (
        <div className="absolute right-0 z-50 mt-1.5 w-[430px] rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="flex items-start justify-between border-b border-slate-100 px-3 py-2">
            <div>
              <div className="text-[12px] font-semibold text-slate-800">Veri tutarlılığı</div>
              <div className="text-[10px] text-slate-400">
                Rapordaki rakamlar mecraların kendi API'leriyle karşılaştırıldı
                {data?.end && data.end < fmtDate(endDate) ? ` · ${new Date(data.end).toLocaleDateString('tr-TR')} dahil (bugün henüz senkronlanmadı)` : ''}
                {data?.hesaplandi ? ` · ${new Date(data.hesaplandi).toLocaleString('tr-TR')}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => getir(true)} disabled={yukleniyor}
                className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-40" title="Yeniden ölç">
                <RefreshCw size={12} className={yukleniyor ? 'animate-spin' : ''} />
              </button>
              <button type="button" onClick={() => setAcik(false)} className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600">
                <X size={12} />
              </button>
            </div>
          </div>

          <div className="max-h-[340px] overflow-y-auto px-3 py-2">
            {hata && <div className="py-3 text-[11px] text-rose-600">{hata}</div>}
            {!hata && !data && <div className="py-3 text-[11px] text-slate-400">Ölçülüyor…</div>}
            {data?.kontroller?.length ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-slate-400">
                    <th className="pb-1 text-left font-medium">Kontrol</th>
                    <th className="pb-1 text-right font-medium">Raporda</th>
                    <th className="pb-1 text-right font-medium">API</th>
                    <th className="pb-1 text-right font-medium">Uyum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.kontroller.map((k, i) => (
                    <tr key={i} className="border-t border-slate-50">
                      <td className="py-1 text-[11px] text-slate-700">{adlandir(k.ad)}</td>
                      {k.tip === 'hata' ? (
                        <td colSpan={3} className="py-1 text-right text-[10px] text-amber-600">{k.mesaj}</td>
                      ) : (
                        <>
                          <td className="py-1 text-right text-[11px] tabular-nums text-slate-600">{tr(k.bizde)}</td>
                          <td className="py-1 text-right text-[11px] tabular-nums text-slate-600">{tr(k.api)}</td>
                          <td className={`py-1 text-right text-[11px] font-medium tabular-nums ${
                            k.skor >= 0.98 ? 'text-emerald-600' : k.skor >= 0.95 ? 'text-amber-600' : 'text-rose-600'}`}>
                            %{(k.skor * 100).toFixed(1)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] leading-relaxed text-slate-400">
              Harcama ve trafik, mecraların kendi API'lerinden dip toplam olarak çekilir.
              %2'ye kadar sapma normaldir (platformlar rakamı geriye dönük düzeltir).
              GA4 kırılımlı sorulduğunda arayüz toplamından %1–3 farklı olur, bu hata değildir.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
